import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Progress } from './ui/progress';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Database, Info, Package, CheckCircle2, Loader2, Edit, GripVertical, Trash2, Plus, Save, AlertTriangle, Diamond, Globe, Wrench, Landmark, Zap, Settings } from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { 
  generateTemplateConfigItems, 
  getTemplatePhaseLabels,
  getTemplateMethodologyDescription 
} from '../utils/templateConfigItems';
import type { ConfigItem, ConfigPhase, TemplateType } from '../types';
import { toast } from 'sonner';

interface TemplateDefaultConfigManagerProps {
  accessToken: string;
  currentUser?: any;
}

const TEMPLATE_NAMES: Record<string, string> = {
  'atlassian': 'Atlassian Jira Service Management',
  'servicenow': 'ServiceNow ITSM',
  'ivanti': 'Ivanti Service Manager',
  'bmc-helix': 'BMC Helix ITSM',
  'freshservice': 'Freshservice',
  'custom': 'Custom ITSM'
};

const TEMPLATE_ICONS: Record<string, React.ReactNode> = {
  'atlassian': <Diamond className="size-4 text-blue-600" />,
  'servicenow': <Globe className="size-4 text-green-600" />,
  'ivanti': <Wrench className="size-4 text-orange-600" />,
  'bmc-helix': <Landmark className="size-4 text-purple-600" />,
  'freshservice': <Zap className="size-4 text-yellow-600" />,
  'custom': <Settings className="size-4 text-gray-600" />
};

export function TemplateDefaultConfigManager({ accessToken, currentUser }: TemplateDefaultConfigManagerProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>('atlassian');
  const [configItems, setConfigItems] = useState<ConfigItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSeeded, setIsSeeded] = useState(false);
  const [seedProgress, setSeedProgress] = useState({ current: 0, total: 0 });
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ConfigItem | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    defaultPhase: 1 as ConfigPhase,
    coreFeature: false,
    requiresAssets: false,
    enabled: true
  });

  useEffect(() => {
    checkIfSeeded();
  }, [selectedTemplate]);

  const checkIfSeeded = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/defaults?template=${selectedTemplate}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const templateItems = data.items?.filter((item: ConfigItem) => 
          item.id?.startsWith(`${selectedTemplate}-config-`)
        ) || [];
        setConfigItems(templateItems);
        setIsSeeded(templateItems.length > 0);
      }
    } catch (error) {
      console.error('Error checking template seed status:', error);
    }
  };

  const handleSeedTemplate = async () => {
    const templateName = TEMPLATE_NAMES[selectedTemplate] || selectedTemplate;
    
    if (!confirm(`This will add the default configuration items for ${templateName}. This template includes ${getTemplateConfigItems(selectedTemplate).length} items. Continue?`)) {
      return;
    }

    setLoading(true);
    const defaultItems = generateTemplateConfigItems(selectedTemplate);
    setSeedProgress({ current: 0, total: defaultItems.length });
    
    try {
      let successCount = 0;
      for (let i = 0; i < defaultItems.length; i++) {
        const item = defaultItems[i];
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/defaults`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ item }),
          }
        );
        
        if (response.ok) successCount++;
        setSeedProgress({ current: i + 1, total: defaultItems.length });
      }
      
      toast.success(`Added ${successCount} of ${defaultItems.length} configuration items for ${templateName}`);
      await checkIfSeeded();
    } catch (error) {
      console.error('Error seeding template defaults:', error);
      toast.error('Error seeding template configuration items. Please try again.');
    } finally {
      setLoading(false);
      setSeedProgress({ current: 0, total: 0 });
    }
  };

  const openEditDialog = (item: ConfigItem) => {
    setEditingItem(item);
    setEditForm({
      name: item.name,
      description: item.description || '',
      defaultPhase: item.defaultPhase,
      coreFeature: item.coreFeature || false,
      requiresAssets: item.requiresAssets || false,
      enabled: item.enabled !== false
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    
    // Show warning about global changes
    if (!confirm(`WARNING: These changes will apply to the ${TEMPLATE_NAMES[selectedTemplate]} template GLOBALLY.\n\nThis will affect:\n• Any companies that apply this template in the future\n• Companies will NOT be affected if they have already applied this template\n\nAre you sure you want to save these changes?`)) {
      return;
    }
    
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/defaults/${editingItem.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...editingItem,
            ...editForm
          }),
        }
      );

      if (response.ok) {
        toast.success('Configuration item updated successfully');
        setEditDialogOpen(false);
        setEditingItem(null);
        await checkIfSeeded();
      } else {
        toast.error('Failed to update configuration item');
      }
    } catch (error) {
      console.error('Error updating config item:', error);
      toast.error('Error updating configuration item');
    }
  };

  const handleDeleteItem = async (item: ConfigItem) => {
    if (!confirm(`Are you sure you want to delete "${item.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/defaults/${item.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Configuration item deleted successfully');
        await checkIfSeeded();
      } else {
        toast.error('Failed to delete configuration item');
      }
    } catch (error) {
      console.error('Error deleting config item:', error);
      toast.error('Error deleting configuration item');
    }
  };
  
  const phaseLabels = getTemplatePhaseLabels(selectedTemplate);
  const methodology = getTemplateMethodologyDescription(selectedTemplate);
  
  // Count items by phase
  const itemsByPhase: Record<number, number> = {};
  configItems.forEach(item => {
    itemsByPhase[item.defaultPhase] = (itemsByPhase[item.defaultPhase] || 0) + 1;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3>Template-Specific Configuration Items</h3>
          <p className="text-muted-foreground">
            Seed default configuration items for specific consulting templates
          </p>
        </div>
      </div>

      {/* Template Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Select Template</CardTitle>
          <CardDescription>
            Choose a template to view or seed its default configuration items
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="w-full max-w-md">
            <Select value={selectedTemplate} onValueChange={(v) => setSelectedTemplate(v as TemplateType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(TEMPLATE_NAMES).map(([key, name]) => (
                  <SelectItem key={key} value={key}>
                    <span className="flex items-center gap-2">
                      <span>{TEMPLATE_ICONS[key]}</span>
                      <span>{name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Methodology Description */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Methodology:</strong> {methodology}
            </AlertDescription>
          </Alert>

          {/* Status & Actions */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              {isSeeded ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium">Template Seeded</p>
                    <p className="text-muted-foreground">
                      {configItems.length} configuration items loaded
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <Package className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Template Not Seeded</p>
                    <p className="text-muted-foreground">
                      Click to add default configuration items
                    </p>
                  </div>
                </>
              )}
            </div>
            <Button 
              onClick={handleSeedTemplate} 
              disabled={loading || isSeeded}
              variant={isSeeded ? "outline" : "default"}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Seeding...
                </>
              ) : (
                <>
                  <Database className="mr-2 h-4 w-4" />
                  {isSeeded ? 'Already Seeded' : 'Seed Template'}
                </>
              )}
            </Button>
          </div>

          {/* Progress Indicator */}
          {loading && seedProgress.total > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-muted-foreground">
                  Seeding configuration items...
                </span>
                <span className="font-medium">
                  {seedProgress.current} / {seedProgress.total}
                </span>
              </div>
              <Progress 
                value={(seedProgress.current / seedProgress.total) * 100} 
                className="h-2"
              />
              <p className="text-xs text-muted-foreground text-center">
                This may take 30-60 seconds. Please don't close this page.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template Overview */}
      {isSeeded && (
        <Card>
          <CardHeader>
            <CardTitle>
              {TEMPLATE_ICONS[selectedTemplate]} {TEMPLATE_NAMES[selectedTemplate]} Overview
            </CardTitle>
            <CardDescription>
              Configuration items breakdown by phase
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(phaseLabels).map(([phase, label]) => {
                const count = itemsByPhase[parseInt(phase)] || 0;
                return (
                  <Card key={phase}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{label}</p>
                          <p className="text-muted-foreground">
                            {count} item{count !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-lg">
                          {count}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Items List */}
      {isSeeded && (
        <Card>
          <CardHeader>
            <CardTitle>Configuration Items Detail</CardTitle>
            <CardDescription>
              All configuration items for this template
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {Object.entries(phaseLabels).map(([phase, label]) => {
                const phaseItems = configItems.filter(item => item.defaultPhase === parseInt(phase));
                
                if (phaseItems.length === 0) return null;
                
                return (
                  <div key={phase} className="space-y-3">
                    <h4 className="flex items-center gap-2">
                      <Badge variant="outline">{label}</Badge>
                      <span className="text-muted-foreground">({phaseItems.length} items)</span>
                    </h4>
                    <div className="grid gap-2">
                      {phaseItems.map((item, idx) => (
                        <div 
                          key={item.id} 
                          className="flex items-start gap-3 p-3 border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                              {idx + 1}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <p className="font-medium">{item.name}</p>
                                {item.description && (
                                  <p className="text-muted-foreground mt-1">{item.description}</p>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {item.coreFeature && (
                                  <Badge variant="default" className="text-xs">Core</Badge>
                                )}
                                {item.requiresAssets && (
                                  <Badge variant="secondary" className="text-xs">Assets</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              onClick={() => openEditDialog(item)}
                              variant="outline"
                              size="icon"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              onClick={() => handleDeleteItem(item)}
                              variant="outline"
                              size="icon"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Configuration Item</DialogTitle>
            <DialogDescription>
              Make changes to the configuration item details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input 
                id="name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea 
                id="description"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="col-span-3"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="defaultPhase">Default Phase</Label>
              <Select 
                value={editForm.defaultPhase.toString()}
                onValueChange={(v) => setEditForm({ ...editForm, defaultPhase: parseInt(v) as ConfigPhase })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(phaseLabels).map(([phase, label]) => (
                    <SelectItem key={phase} value={phase}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="coreFeature">Core Feature</Label>
              <Switch 
                id="coreFeature"
                checked={editForm.coreFeature}
                onCheckedChange={(checked) => setEditForm({ ...editForm, coreFeature: checked })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="requiresAssets">Requires Assets</Label>
              <Switch 
                id="requiresAssets"
                checked={editForm.requiresAssets}
                onCheckedChange={(checked) => setEditForm({ ...editForm, requiresAssets: checked })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="enabled">Enabled</Label>
              <Switch 
                id="enabled"
                checked={editForm.enabled}
                onCheckedChange={(checked) => setEditForm({ ...editForm, enabled: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              onClick={handleSaveEdit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Helper function to get template config items count
function getTemplateConfigItems(templateType: string): any[] {
  const items = generateTemplateConfigItems(templateType);
  return items;
}